import { useMelwaysInfo } from '@seek/melways-react';
import { useTranslations } from '@vocab/react';
import {
  Column,
  Columns,
  Heading,
  Stack,
  Text,
  TextLink,
} from 'braid-design-system';

import translations from './.vocab';
import { BeDiscovered } from './images/be-discovered';
import { GetPersonalMatches } from './images/get-personal-matches';
import { StandoutWithTrustedProfile } from './images/standout-with-trusted-profile';

const constructSeekPassUrl = (country: string | null, locale: string) => {
  const localeLower = locale.toLowerCase();
  // For Indonesia and Thailand with English selected, include the locale path (e.g., /id/en-id, /th/en-th)
  if ((country === 'id' || country === 'th') && localeLower.startsWith('en')) {
    return `https://seekpass.co/${country}/${localeLower}`;
  }
  return `https://seekpass.co/${country}`;
};

const InfoItem = ({
  Icon,
  title,
  description,
}: {
  Icon: () => JSX.Element;
  title: string;
  description: React.ReactNode;
}) => (
  <Column width="1/3">
    <Stack space="large" align="center">
      <Icon />
      <Stack space="medium" align="center">
        <Heading level="4" align="center">
          {title}
        </Heading>
        <Text align="center">{description}</Text>
      </Stack>
    </Stack>
  </Column>
);

export const SearchLandingPageInfo = () => {
  const { t } = useTranslations(translations);
  const { locale, country } = useMelwaysInfo();
  const seekPassUrl = constructSeekPassUrl(country, locale);

  return (
    <Columns space="xxlarge" collapseBelow="tablet" align="center" alignY="top">
      <InfoItem
        Icon={BeDiscovered}
        title={t('BE_DISCOVERED')}
        description={t('BE_DISCOVERED_DESCRIPTION')}
      />
      <InfoItem
        Icon={GetPersonalMatches}
        title={t('GET_PERSONAL_MATCHES')}
        description={t('GET_PERSONAL_MATCHES_DESCRIPTION')}
      />
      <InfoItem
        Icon={StandoutWithTrustedProfile}
        title={t('STANDOUT_WITH_TRUSTED_PROFILE')}
        description={t('STANDOUT_WITH_TRUSTED_PROFILE_DESCRIPTION', {
          Link: (children) => (
            <TextLink weight="weak" href={seekPassUrl} target="_blank">
              {children}
            </TextLink>
          ),
        })}
      />
    </Columns>
  );
};
